import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpsCall from '@/services/httpsCall';
import logger from '@/utils/logger';
import type { Genie } from '@/types/types';

type HelpTicketInput = {
  message: string | null;
  store_location_id: string | null;
};

export interface GenieRequests {
  content: any[];
  page: {
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
    paged: boolean;
  };
}

// * ====================== Fetch All Genie Requests ====================== *
const fetchAllGenieRequests = async ({
  page = 0,
  size = 10,
  keyword = '',
  sortBy = '',
  sortDir = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc' | '';
}): Promise<GenieRequests> => {
  try {
    const response = await httpsCall.get(
      `/superadmin/request-list?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
    return response.data?.data as GenieRequests;
  } catch (error: any) {
    logger.error('Error fetching owners:', error);
    throw error;
  }
};

export const useFetchAllGenieRequests = (
  page: number = 0,
  size: number = 10,
  keyword = '',
  sortBy: string = '',
  sortDir: 'asc' | 'desc' | '' = ''
) => {
  return useQuery<GenieRequests, Error>({
    queryKey: ['help_tickets', page, size, keyword, sortBy, sortDir],
    queryFn: () => fetchAllGenieRequests({ page, size, keyword, sortBy, sortDir }),
  });
};

// * ====================== Fetch Single Genie Requests ====================== *
const fetchSingleGenieRequest = async (ticketId: string): Promise<Genie | null> => {
  const response = await httpsCall.get(`/superadmin/request/${ticketId}`);
  logger.log('response', response);
  return response?.data?.data;
};

export const useFetchSingleGenieRequest = (ticketId: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['help_ticket', ticketId],
    queryFn: () => fetchSingleGenieRequest(ticketId),
    enabled: !!ticketId && enabled,
    retry: false,
  });
};

// * ====================== Create Help Ticket ====================== *
export const createHelpTicket = async (helpTicketData: HelpTicketInput) => {
  const response = await httpsCall.post('/user/genie-request', {
    store: {
      id: helpTicketData?.store_location_id,
    },
    messsage: helpTicketData?.message,
  });
  return response.data;
};

export const useCreateHelpTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createHelpTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help_tickets'], exact: false });
    },
  });
};
